import type { CustomerListRow, KycStatus } from "@/modules/customers/domain/types";
import { Badge } from "@/components/ui/badge";
import { formatDateTimeLocal } from "@/shared/lib/date-time";
import { formatPhoneDisplay } from "@/shared/lib/phone";
import { DataTable, createSortableColumn, type TanstackReactTable } from "@/shared/ui/data-table";
import { Button } from "@/components/ui/button";


import { EyeIcon } from "lucide-react";

const KYC_STATUS_LABELS: Record<KycStatus, string> = {
  NONE: "ຍັງບໍ່ຍື່ນ",
  PENDING: "ລໍຖ້າກວດ",
  VERIFIED: "ຢັ້ງຢືນແລ້ວ",
  REJECTED: "ບໍ່ຜ່ານ",
};

const KYC_STATUS_BADGE: Record<
  KycStatus,
  "default" | "secondary" | "destructive" | "outline"
> = {
  NONE: "outline",
  PENDING: "secondary",
  VERIFIED: "default",
  REJECTED: "destructive",
};

type CustomersTableProps = {
  data: CustomerListRow[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
  onPaginationChange: (offset: number, limit: number) => void;
  onView: (id: string) => void;
};

export function CustomersTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  sortBy,
  sortOrder,
  onSortingChange,
  onPaginationChange,
  onView,
}: CustomersTableProps) {
  const columns: TanstackReactTable.ColumnDef<CustomerListRow>[] = [
    createSortableColumn<CustomerListRow>("phone", "ເບີໂທ", {
      size: 130,
      cell: ({ row }) => (
        <span className="font-medium tabular-nums tracking-tight">
          {formatPhoneDisplay(row.original.phone)}
        </span>
      ),
    }),
    createSortableColumn<CustomerListRow>("name", "ຊື່", {
      size: 180,
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {row.original.name ?? "–"}
        </span>
      ),
    }),
    {
      id: "kycStatus",
      header: "KYC",
      size: 120,
      cell: ({ row }) => (
        <Badge variant={KYC_STATUS_BADGE[row.original.kycStatus]}>
          {KYC_STATUS_LABELS[row.original.kycStatus]}
        </Badge>
      ),
      enableSorting: false,
    },
    createSortableColumn<CustomerListRow>("createdAt", "ວັນທີລົງທະບຽນ", {
      size: 170,
      cell: ({ row }) => formatDateTimeLocal(row.original.createdAt),
    }),
    {
      id: "actions",
      header: "",
      cell: ({ row }) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onView(row.original.id)}
        >
          <EyeIcon className="size-4" />
          ລາຍລະອຽດ
        </Button>
      ),
      size: 130,
      enableSorting: false,
    },
  ];

  return (
    <DataTable<CustomerListRow, unknown>
      noDataMessage="ບໍ່ພົບລູກຄ້າ"
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortingChange={(sorting) => {
        if (sorting[0]?.id === "") return;
        onSortingChange(sorting[0]?.id as string, !!sorting[0]?.desc);
      }}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
    />
  );
}
